import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AdvancedFilterButton, FilterPanelCollapse } from '@/components/common/AdvancedFilter';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { DataTable } from '@/components/common/DataTable';
import { DateRangeBadge } from '@/components/common/DateRangeBadge';
import { ErrorState } from '@/components/common/ErrorState';
import { StatCardGrid } from '@/components/common/StatCardGrid';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { offlineDcrInfiniteQueryOptions, transportTypeQueryOptions } from '../api/billing.api';
import { BillingFilterPanel } from '../components/BillingFilterPanel';
import { DcrEntryDetailsModal } from '../components/DcrEntryDetailsModal';
import { getDcrQueueColumns } from '../components/DcrQueueColumns';
import { DcrDraftsContext, type DcrRowDraft } from '../components/queueDraftsContext';
import {
  BILLING_SEARCH_PLACEHOLDER,
  QUEUE_FIT_CONTENT_COLUMN_IDS,
} from '../constants/billing.constants';
import { useOfflineDcrFilters } from '../hooks/useOfflineDcrFilters';
import { getVerifyStatusFilterField, getWorkTypeFilterField } from '../utils/billingFilterFields';
import { getClaimStatCards, getLimitStatCards } from '../utils/billingStatCards';
import { useVerifyDcr } from '../hooks/useVerifyEntry';
import type { OfflineDcrRow } from '../types/billing.types';

interface PendingDecision {
  row: OfflineDcrRow;
  accept: boolean;
}

export function OfflineDcrVerifyPage() {
  const controller = useOfflineDcrFilters();
  const { rows, firstPageMeta, isLoading, loadError, refetch, loadMoreProps } =
    useInfiniteTableQuery(offlineDcrInfiniteQueryOptions(controller.params));
  const limits = firstPageMeta?.limits;
  const { data: transportTypes, isLoading: transportTypesLoading } = useQuery(
    transportTypeQueryOptions(),
  );
  const verifyDcr = useVerifyDcr();

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [drafts, setDrafts] = useState<Record<number, DcrRowDraft>>({});
  const [decision, setDecision] = useState<PendingDecision | null>(null);
  const [detailsRecordId, setDetailsRecordId] = useState<number | null>(null);

  const handleDraftChange = useCallback((recordId: number, patch: Partial<DcrRowDraft>) => {
    setDrafts((prev) => ({
      ...prev,
      [recordId]: {
        approveFareAmount: patch.approveFareAmount ?? prev[recordId]?.approveFareAmount ?? '',
        transportTypeId:
          'transportTypeId' in patch ? patch.transportTypeId : prev[recordId]?.transportTypeId,
      },
    }));
  }, []);

  const { search } = controller.params;
  const pendingRecordId = verifyDcr.isPending ? (verifyDcr.variables?.recordId ?? null) : null;
  const columns = useMemo(() => {
    return getDcrQueueColumns({
      mode: 'verify',
      limits,
      transportTypes,
      transportTypesLoading,
      onDecide: (row, accept) => setDecision({ row, accept }),
      onOpenDetails: setDetailsRecordId,
      pendingRecordId,
      searchText: search,
    });
  }, [limits, transportTypes, transportTypesLoading, pendingRecordId, search]);

  function handleConfirm() {
    if (!decision) return;

    const draft = drafts[decision.row.recordId];
    const approveFareAmount = Number(draft?.approveFareAmount ?? decision.row.fareAmount);
    const transportTypeId = draft?.transportTypeId ?? decision.row.transportTypeId ?? undefined;

    verifyDcr.mutate(
      {
        recordId: decision.row.recordId,
        payload: decision.accept
          ? { accept: true, approveFareAmount, ...(transportTypeId && { transportTypeId }) }
          : { accept: false },
      },
      {
        onSettled: () => {
          setDecision(null);
          setDrafts((prev) => {
            const next = { ...prev };
            delete next[decision.row.recordId];
            return next;
          });
        },
      },
    );
  }

  function describeDecision(): string {
    if (!decision) return '';

    const approved = drafts[decision.row.recordId]?.approveFareAmount ?? decision.row.fareAmount;

    return decision.accept
      ? `${decision.row.name}'s entry will be verified at ${approved} Tk.`
      : `${decision.row.name}'s entry will be rejected and keep the claimed fare of ${decision.row.fareAmount} Tk.`;
  }

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <PageHeader
        title="Offline DCR Verify"
        search={{
          value: controller.searchText,
          onChange: controller.setSearchText,
          placeholder: BILLING_SEARCH_PLACEHOLDER,
        }}
        titleAddon={
          <DateRangeBadge dateFrom={controller.params.dateFrom} dateTo={controller.params.dateTo} />
        }
        actions={
          <AdvancedFilterButton
            isOpen={isFilterOpen}
            activeCount={controller.activeFilterCount}
            onClick={() => setIsFilterOpen((wasOpen) => !wasOpen)}
          />
        }
        panel={
          <FilterPanelCollapse isOpen={isFilterOpen}>
            <BillingFilterPanel
              filters={controller}
              fieldsBeforeGeo={[getVerifyStatusFilterField()]}
              fieldsAfterGeo={[getWorkTypeFilterField()]}
            />
          </FilterPanelCollapse>
        }
      />

      {loadError ? (
        <ErrorState
          error={loadError}
          fallbackTitle="Could not load the verification queue"
          onRetry={() => void refetch()}
          fill
        />
      ) : (
        <>
          <StatCardGrid
            isLoading={isLoading}
            stats={[
              ...getClaimStatCards('dcr', firstPageMeta),
              ...getLimitStatCards('dcr', limits),
            ]}
          />

          <DcrDraftsContext.Provider value={{ drafts, onDraftChange: handleDraftChange }}>
            <DataTable
              data={rows}
              columns={columns}
              isLoading={isLoading}
              className="lg:flex-1"
              stickyColumnId="name"
              fitContentColumnIds={QUEUE_FIT_CONTENT_COLUMN_IDS}
              emptyMessage="Nothing waiting to be verified"
              emptyHint="Try a wider date range or clear the filters."
              {...loadMoreProps}
            />
          </DcrDraftsContext.Provider>
        </>
      )}

      <DcrEntryDetailsModal recordId={detailsRecordId} onClose={() => setDetailsRecordId(null)} />

      <ConfirmDialog
        open={decision !== null}
        onOpenChange={(open) => {
          if (!open) setDecision(null);
        }}
        title={decision?.accept ? 'Verify this entry?' : 'Reject this entry?'}
        description={describeDecision()}
        confirmLabel={decision?.accept ? 'Verify' : 'Reject'}
        confirmVariant={decision?.accept ? 'default' : 'destructive'}
        isPending={verifyDcr.isPending}
        pendingLabel="Saving"
        onConfirm={handleConfirm}
      />
    </div>
  );
}
